import { mapActions, mapState } from 'vuex';
import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import {
  convertGQLToProductJson,
  formatPrice,
  htmlDecode,
} from 'utils/general';
import { i18n } from 'lib/i18n.js';
import { getAllValueForTag } from '../utils/general';

export default {
  data() {
    return {
      bundleProductsData: [],
      isBundle: false,
      date: Date.now(),
      randomNumber: Math.floor(Math.random() * (1000000 - 1 + 1)) + 1,
      bundleFullPrice: null,
    };
  },
  computed: {
    ...mapState('global', ['cart']),
    bundleFullPriceText() {
      if (!this.bundleFullPrice) return '';
      return this.$t('products.product.bundle_price', {
        price: formatPrice(this.bundleFullPrice, true),
      });
    },
    bundleFullPriceHtml() {
      if (
        !this.bundleFullPrice ||
        Number(this.bundleFullPrice) === Number(this.productObject.price)
      )
        return '';
      return `<span>${formatPrice(this.bundleFullPrice, true)}</span>`;
    },
  },
  methods: {
    ...mapActions('cart', ['addBundleItemsToCart']),
    getBundleUid(productObject, bundle) {
      return bundle.reduce(
        (uid, product) =>
          `${uid}_${
            product.variants[0] ? product.variants[0].id : product.variants.id
          }`,
        `${productObject.id}`,
      );
    },
    getBundleSKU(bundle) {
      return bundle.reduce((uid, product) => {
        return `${uid}${
          product.variants[0] ? product.variants[0].sku : product.variants.sku
        },`;
      }, '');
    },
    getBundleIdFromProduct(product) {
      let bundleId = product.variants[0]
        ? product.variants[0].id
        : product.variants.id;

      if (product.variants.length > 1) {
        const chosenVariant = product.variants.find(
          (variant) => variant.isBundleVariant,
        );
        if (chosenVariant) {
          bundleId = chosenVariant.id;
        }
      }
      return bundleId;
    },
    async fetchBundleProductsByHandles(bundleProductHandles) {
      let storefrontResponse = null;
      let tempProductSlug = '';
      const bundle = [];

      await Promise.all(
        bundleProductHandles?.map(async (productSlug, i) => {
          if (productSlug.includes('|')) {
            tempProductSlug = productSlug.split('|');
            storefrontResponse = await storefront.request(gqlQueries.product, {
              handle: tempProductSlug[0],
            });
          } else {
            storefrontResponse = await storefront.request(gqlQueries.product, {
              handle: productSlug,
            });
          }
          const bundleProduct = convertGQLToProductJson(
            storefrontResponse.product,
          );
          const bundleVariant = bundleProduct.variants.find(
            (variant) => variant.isBundleVariant === 'true',
          );
          if (bundleVariant) {
            bundle[i] = {
              ...bundleProduct,
              variant: bundleVariant,
              price: bundleVariant.price,
              bundleVariant,
            };
          } else {
            bundle[i] = bundleProduct;
          }
        }),
      );

      this.bundleProductsData = bundle;
      this.getBundleFullPrice();
    },
    async getBundleHashes() {
      return await fetch(
        `${Shopify.routes.root}products/${this.productObject.handle}?view=virtual-bundle-hashes`,
      )
        .then((response) => response.text())
        .then((response) => {
          const product = JSON.parse(response);
          return product;
        })
        .catch(() => {
          console.warn(
            `Error fetching data from /products/${this.productObject.handle}`,
          );
        });
    },
    getBundleFullPrice() {
      this.bundleFullPrice = this.bundleProductsData
        .filter((item) => item)
        .reduce((total, item) => {
          if (item.variants) {
            const bundleVariant = item.variants.find(
              (variant) => variant.isBundleVariant === 'true',
            );
            if (bundleVariant) {
              return total + parseInt(bundleVariant.price, 10);
            }
            return total + parseInt(item.variants[0].price, 10);
          }
          return total + parseInt(item.price, 10);
        }, 0);
    },
    async addBundleToCart({
      productObject = {},
      price = null,
      title = null,
      quantity = 1,
      properties = {},
      callback = () => {},
      bundle = null,
    }) {
      const calcBundle = bundle ? bundle : this.bundleProductsData;
      const bundleUid = this.getBundleUid(productObject, calcBundle);
      let bundleSKUs = this.getBundleSKU(calcBundle);
      if (bundleSKUs.length > 0) {
        bundleSKUs = bundleSKUs.substring(0, bundleSKUs.length - 1);
      }

      const hashes = await this.getBundleHashes();
      this.addBundleItemsToCart({
        lines: calcBundle.reduce(
          ({ lines, index }, product) => {
            lines.push({
              id: this.getBundleIdFromProduct(product),
              quantity: this.quantity,
              properties: {
                ...properties,
                _bundle: productObject.id,
                _bundle_uid: bundleUid,
                _bundle_cart_uid: `${bundleUid}-${this.date}-${this.randomNumber}`,
                _bundle_index: index,
                _bundle_price: Math.round(parseFloat(price)),
                _bundle_title: htmlDecode(title),
                _bundle_image: productObject.featuredImage,
                _bundle_compare_price:
                  productObject.variants[0].compare_at_price,
                _bundle_url: productObject.url,
                _bundle_product_title: htmlDecode(product.title),
                _bundle_product_price: product.price,
                _bundle_hash: hashes[product.handle],
                _product_handle: product.handle,
                _product_rating:
                  product.reviewsAverage ||
                  product.metafields?.yotpo?.reviews_average,
              },
            });
            if (index === 0) {
              bundleSKUs = bundleSKUs.replace(
                `${
                  product.variants.length
                    ? product.variants[0].sku
                    : product.variants.sku
                },`,
                '',
              );
              lines[0].properties._SubProductsIds = bundleSKUs;
            }
            index++;
            return { lines, index };
          },
          { lines: [], index: 0 },
        ).lines,
        quantity: quantity,
        bundleProduct: productObject,
        bundles: calcBundle,
        otherData: {
          origin: properties._origin,
          originProduct: properties._origin_product,
          listId: properties._item_list_id,
          listName: properties._item_list_name,
          index: properties._index,
          bundleType: 'virtual bundle',
          bundleFullPrice: this.bundleFullPrice,
        },
        callback,
      });
    },
    isBundleUnavailable() {
      return this.bundleProductsData
        .filter((item) => item)
        .some((item) => {
          let bundleVariant = item?.bundleVariant
            ? item.bundleVariant
            : item.variants?.find(
                (variant) => variant.isBundleVariant === 'true',
              );

          if (bundleVariant) {
            return !bundleVariant.available;
          }

          return !item.variants[0].available;
        });
    },
  },
  i18n,
};
